/**
 * Utility module for handling chart errors consistently across controllers
 */

/**
 * Mixin to add error-aware chart rendering to chart controllers
 * 
 * @param {Class} BaseController - The base controller class to extend
 * @returns {Class} - A new class with error-aware chart rendering
 */
export function withErrorHandling(BaseController) {
  return class extends BaseController {
    renderChart(data) {
      // If there was an error, don't try to render the chart
      if (this.hasError) {
        // Remove loading class when displaying the error
        this.chartTarget.classList.remove('chart-loading');
        return;
      }
      
      // Call the parent's renderChart method
      super.renderChart(data);
    }
  };
}

/**
 * Handle errors in chart data and display an appropriate message
 * 
 * @param {Object} data - The chart data that might contain an error
 * @param {HTMLElement} chartTarget - The chart target element to display error in
 * @returns {Object} - An object with hasError flag and the formatted data
 */
export function handleChartError(data, chartTarget) {
  // Check if there's an error in the data
  if (data && data.error) {
    // Create the error container
    const errorElement = document.createElement('div');
    errorElement.className = 'mt-3 text-muted';
    
    // Create the alert icon
    const iconElement = document.createElement('i');
    iconElement.className = 'fe fe-alert-circle';
    iconElement.style.marginRight = '8px';
    
    // Add the icon and message text
    errorElement.appendChild(iconElement);
    errorElement.appendChild(document.createTextNode(data.error));
    
    // Clear the chart target while preserving it
    chartTarget.innerHTML = '';
    chartTarget.appendChild(errorElement);
    
    // Remove loading class when displaying the error
    chartTarget.classList.remove('chart-loading');
    
    // Return empty data and error flag
    return { 
      hasError: true, 
      formattedData: [] 
    };
  }
  
  // No error, return flag only
  return { 
    hasError: false 
  };
}